import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import Div from "@jumbo/shared/Div";
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  FormHelperText,
} from "@mui/material";
import useMetadata from "app/hooks/useMetadata";
import reportService from "app/services/report-service";
import ContentHeader from "components/ContentHeader";
import FormActionButtons from "components/ActionButtons/FormActionButtons";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSnackbar } from "notistack";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import useMetaTitle from "app/hooks/useMetaTitle";

const Report = () => {
  const navigate = useNavigate();
  const { metadata } = useMetadata();
  const { enqueueSnackbar } = useSnackbar();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    requestType: "",
    contactNumber: "",
    issue: "",
    image: null,
    imageBase64: null,
  });
  const [imageError, setImageError] = useState("");
  const [commentError, setCommentError] = useState("");
  const [contactNumberError, setContactNumberError] = useState("");
  const [requestTypeError, setRequestTypeError] = useState("");

  const validateContactNumber = (number) => {
    if (!number) {
      return "Contact number is required";
    }
    if (!/^\d+$/.test(number)) {
      return "Contact number must contain only digits";
    }
    if (number.length !== 10) {
      return "Contact number must be exactly 10 digits";
    }
    return "";
  };

  const validateRequestType = (requestType) => {
    if (!requestType) {
      return "Request type is required";
    }
    return "";
  };

  const validateIssue = (issue) => {
    if (issue.length < 20) {
      return "Please enter at least 20 characters";
    }
    return "";
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Validate fields in real-time
    if (name === "requestType") {
      setRequestTypeError(validateRequestType(value));
    } else if (name === "contactNumber") {
      setContactNumberError(validateContactNumber(value));
    } else if (name === "issue") {
      setCommentError(validateIssue(value));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setImageError("No file selected. Please try again.");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image size should not exceed 5MB");
      return;
    }
    if (!file.type.startsWith("image/")) {
      setImageError("Please upload a valid image file");
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setFormData((prev) => ({
        ...prev,
        image: file,
        imageBase64: reader.result.split(",")[1],
      }));
      setImageError("");
    };
    reader.onerror = () => {
      setImageError("Failed to read the file. Please try again.");
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async () => {
    const commentValidationError = validateIssue(formData.issue);
    const contactNumberValidationError = validateContactNumber(
      formData.contactNumber
    );
    const requestTypeValidationError = validateRequestType(
      formData.requestType
    );

    setCommentError(commentValidationError);
    setContactNumberError(contactNumberValidationError);
    setRequestTypeError(requestTypeValidationError);

    if (
      commentValidationError ||
      contactNumberValidationError ||
      requestTypeValidationError
    ) {
      enqueueSnackbar("Please correct the errors in the form", {
        variant: "error",
      });
      return;
    }

    const payload = {
      contactNumber: formData.contactNumber,
      image: formData.imageBase64 || null,
      imageContentType: formData.image ? formData.image.type : null,
      issue: formData.issue,
      requestType: formData.requestType,
      status: "PENDING",
    };

    try {
      setIsLoading(true);
      const response = await reportService.createReport(payload);
      setIsLoading(false);
      if (response) {
        enqueueSnackbar("Report submitted successfully!", {
          variant: "success",
        });
        navigate("/");
      } else {
        enqueueSnackbar("Failed to submit report.", {
          variant: "error",
        });
        navigate(-1);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error submitting report:", error);
      enqueueSnackbar("An error occurred while submitting the report.", {
        variant: "error",
      });
    }
  };

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAction = useCallback(
    ({ id: actionId }) => {
      const actions = {
        back: handleBack,
      };
      actions[actionId]?.();
    },
    [handleBack]
  );

  useMetaTitle({
    title: "Have something to tell us?",
  });

  return (
    <JumboCardQuick
      title={
        <ContentHeader
          isAdmin={false}
          onAction={handleAction}
          allowedActions={["back"]}
          showLeftTabs={true}
        />
      }
      headerSx={{
        borderBottom: 1,
        borderColor: "divider",
        "& .MuiCardHeader-action": { my: -0.75 },
      }}
      wrapperSx={{ p: 0, zIndex: "auto" }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "& .MuiCardContent-root": {
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
        zIndex: "auto",
      }}
    >
      <Div
        sx={{
          p: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          height: "100%",
          justifyContent: "space-between",
          mx: "10%",
          overflow: "visible",
        }}
      >
        <Div>
          <Typography
            variant="h4"
            mb={3}
            sx={{ textAlign: "center", fontSize: "28px" }}
          >
            Have questions or need support?
          </Typography>
          <FormControl fullWidth sx={{ mb: 2 }} error={!!requestTypeError}>
            <InputLabel>Request Type</InputLabel>
            <Select
              name="requestType"
              value={formData.requestType}
              onChange={handleInputChange}
              label="Request Type"
            >
              {metadata?.requestTypes?.map((type) => (
                <MenuItem key={type.key} value={type.key}>
                  {type.value}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>{requestTypeError}</FormHelperText>
          </FormControl>
          <TextField
            fullWidth
            label="Contact Number"
            name="contactNumber"
            value={formData.contactNumber}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            error={!!contactNumberError}
            helperText={contactNumberError}
          />
          <TextField
            fullWidth
            label="Issue/Comments"
            name="issue"
            multiline
            rows={4}
            value={formData.issue}
            onChange={handleInputChange}
            sx={{ mb: 2 }}
            error={!!commentError}
            helperText={commentError}
          />
          <Box sx={{ mb: 2, position: "relative", zIndex: 1000 }}>
            <Button
              variant="contained"
              component="label"
              startIcon={<CloudUploadIcon />}
            >
              Upload
              <input
                hidden
                accept="image/*"
                multiple
                type="file"
                onChange={handleImageChange}
              />
            </Button>
            {formData.image && (
              <Typography
                variant="body2"
                sx={{ mt: 1, color: "text.secondary" }}
              >
                Selected: {formData.image.name}
              </Typography>
            )}
            {imageError && (
              <Typography variant="body2" sx={{ mt: 1, color: "error.main" }}>
                {imageError}
              </Typography>
            )}
          </Box>
        </Div>
        <FormActionButtons
          cancelLabel="Cancel"
          actionLabel="Submit"
          cancelHandler={handleBack}
          actionHandler={handleSubmit}
          isLoading={isLoading}
          isActionDisabled={isLoading}
          mode="submit"
        />
      </Div>
    </JumboCardQuick>
  );
};

export default Report;
